/**
 * Control-status slice — the backend's view of operator control (PROTOCOL.md §4.2/§4.4):
 * who owns control, which input source it accepts, whether the planner is connected,
 * watchdog state — plus the last `error` the backend rejected an operator message with.
 *
 * Framework-free; ingests raw DataChannel messages like the other slices.
 */

import { createStore } from "./store";
import type { StoreApi } from "./store";

export interface ControlStatus {
  owner_sid: string | null;
  active_source: { id: string; kind: string } | null;
  robot_link: "connected" | "disconnected";
  watchdog: "ok" | "tripped" | "idle";
  rearm_required?: boolean;
  last_input_age_ms: number | null;
  input_rate_hz: number;
  monitor?: "live" | "lost" | "not-required";
  requested_mode?: "off" | "keyboard" | "gamepad" | "xr" | null;
  mode_generation?: number;
}

export interface BackendError {
  code: string;
  detail: string;
  ref_event: string | null;
  /** Local receive time (ms, Date.now()). */
  at: number;
}

export interface ControlStatusSnapshot {
  status: ControlStatus | null;
  /** Local receive time of `status` (ms, Date.now()). */
  receivedAt: number | null;
  lastError: BackendError | null;
}

export interface ControlStatusSlice extends Pick<
  StoreApi<ControlStatusSnapshot>,
  "getState" | "getInitialState" | "subscribe"
> {
  ingest(message: unknown): void;
  reset(): void;
  /** ms since the last control-status packet (null if none yet). */
  getAgeMs(): number | null;
}

function parse(message: unknown): Record<string, unknown> | null {
  let raw: unknown = message;
  if (typeof raw !== "string") {
    if (raw instanceof ArrayBuffer) raw = new TextDecoder().decode(new Uint8Array(raw));
    else if (ArrayBuffer.isView(raw)) raw = new TextDecoder().decode(raw);
    else return null;
  }
  try {
    const parsed = JSON.parse(String(raw));
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function normalizeStatus(p: Record<string, unknown>): ControlStatus {
  const src = p.active_source as Record<string, unknown> | null | undefined;
  const watchdog = p.watchdog === "ok" || p.watchdog === "tripped" ? p.watchdog : "idle";
  return {
    owner_sid: typeof p.owner_sid === "string" ? p.owner_sid : null,
    active_source:
      src && typeof src === "object" && typeof src.id === "string"
        ? { id: src.id, kind: String(src.kind ?? "") }
        : null,
    robot_link: p.robot_link === "connected" ? "connected" : "disconnected",
    watchdog,
    rearm_required: p.rearm_required === true,
    last_input_age_ms: typeof p.last_input_age_ms === "number" ? p.last_input_age_ms : null,
    input_rate_hz: typeof p.input_rate_hz === "number" ? p.input_rate_hz : 0,
    ...(p.monitor === "live" || p.monitor === "lost" || p.monitor === "not-required"
      ? { monitor: p.monitor }
      : {}),
    ...(p.requested_mode === null || p.requested_mode === "off" || p.requested_mode === "keyboard"
      || p.requested_mode === "gamepad" || p.requested_mode === "xr"
      ? { requested_mode: p.requested_mode }
      : {}),
    ...(typeof p.mode_generation === "number" ? { mode_generation: p.mode_generation } : {}),
  };
}

const EMPTY: ControlStatusSnapshot = { status: null, receivedAt: null, lastError: null };

export function createControlStatusSlice(now: () => number = Date.now): ControlStatusSlice {
  const store = createStore<ControlStatusSnapshot>(() => EMPTY);

  return {
    getState: store.getState,
    getInitialState: store.getInitialState,
    subscribe: store.subscribe,

    ingest(message) {
      const msg = parse(message);
      if (!msg) return;
      const payload = msg.payload;
      if (!payload || typeof payload !== "object") return;
      const p = payload as Record<string, unknown>;
      if (msg.event === "control-status") {
        store.setState({ status: normalizeStatus(p), receivedAt: now() });
      } else if (msg.event === "error") {
        store.setState({
          lastError: {
            code: String(p.code ?? "unknown"),
            detail: String(p.detail ?? ""),
            ref_event: typeof p.ref_event === "string" ? p.ref_event : null,
            at: now(),
          },
        });
      }
    },

    reset() {
      store.setState(EMPTY, true);
    },

    getAgeMs() {
      const at = store.getState().receivedAt;
      return at === null ? null : now() - at;
    },
  };
}
